import { AnchorTreeNode } from '../types/anchor-tree';

/** Flat node for the canvas: the tree node plus its placement context. */
export interface CanvasNode {
  /** Same as the tree node's uuid — used as the Foblex node id. */
  id: string;
  node: AnchorTreeNode;
  /** undefined for the root. */
  parentId?: string;
  /** Expected value on the branch leading to this node (undefined for root). */
  branchValue?: string;
  depth: number;
}

/** Flat edge between a decision node and one of its branch children. */
export interface CanvasEdge {
  /** `${parentId}->${childId}` — stable as long as node uuids are stable. */
  id: string;
  sourceId: string;
  targetId: string;
  /** The branch's expectedValue ('' when unset). */
  label: string;
}

export interface CanvasGraph {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
}

/**
 * Maps the recursive anchor tree to the flat node/edge lists the canvas
 * renders (document-order DFS, parents before children).
 */
export function toCanvasGraph(root: AnchorTreeNode | null): CanvasGraph {
  const nodes: CanvasNode[] = [];
  const edges: CanvasEdge[] = [];

  const visit = (node: AnchorTreeNode, depth: number, parentId?: string, branchValue?: string): void => {
    nodes.push({ id: node.id, node, parentId, branchValue, depth });
    if (node.kind !== 'decision') {
      return;
    }
    for (const branch of node.branches ?? []) {
      edges.push({
        id: `${node.id}->${branch.child.id}`,
        sourceId: node.id,
        targetId: branch.child.id,
        label: branch.expectedValue
      });
      visit(branch.child, depth + 1, node.id, branch.expectedValue);
    }
  };

  if (root) {
    visit(root, 0);
  }
  return { nodes, edges };
}
